import * as React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/components/protected-route";
import { AppLayout } from "@/components/app-layout";
import { LoginPage } from "@/pages/login-page";
import { OverviewPage } from "@/pages/overview-page";
import { PagesListPage } from "@/pages/pages-list-page";
import { PageDetailPage } from "@/pages/page-detail-page";
import { DomainsListPage } from "@/pages/domains-list-page";
import { DomainPurchasePage } from "@/pages/domain-purchase-page";
import { DomainDetailPage } from "@/pages/domain-detail-page";
import { LeadsPage } from "@/pages/leads-page";
import { VisitsPage } from "@/pages/visits-page";
import { AdSourcesPage } from "@/pages/ad-sources-page";
import { AdminsPage } from "@/pages/admins-page";
import { ConstructorPage } from "@/pages/constructor-page";
import { DraftsListPage } from "@/pages/drafts-list-page";
import { SessionsPage } from "@/pages/sessions-page";
import { MemoryPage } from "@/pages/memory-page";
import { CampaignsListPage } from "@/pages/campaigns-list-page";
import { CampaignDetailPage } from "@/pages/campaign-detail-page";
import { PixelsListPage } from "@/pages/pixels-list-page";
import { AdvertisersListPage } from "@/pages/advertisers-list-page";
import { AdvertiserDetailPage } from "@/pages/advertiser-detail-page";
import { AffiliatesListPage } from "@/pages/affiliates-list-page";
import { AffiliateDetailPage } from "@/pages/affiliate-detail-page";
import { UnassignedPage } from "@/pages/unassigned-page";
import { FinancePage } from "@/pages/finance-page";
import { ConversionsPage } from "@/pages/conversions-page";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 10_000,
        },
    },
});

export function App(): React.ReactNode {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        {/* Constructor is a full-screen route; bypasses AppLayout chrome. */}
                        <Route
                            path="/pages/new"
                            element={
                                <ProtectedRoute>
                                    <ConstructorPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/pages/:id/edit"
                            element={
                                <ProtectedRoute>
                                    <ConstructorPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/constructor/draft/:draftId"
                            element={
                                <ProtectedRoute>
                                    <ConstructorPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            element={
                                <ProtectedRoute>
                                    <AppLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route path="/" element={<OverviewPage />} />
                            <Route path="/pages" element={<PagesListPage />} />
                            <Route path="/pages/:id" element={<PageDetailPage />} />
                            <Route path="/domains" element={<DomainsListPage />} />
                            <Route path="/domains/purchase" element={<DomainPurchasePage />} />
                            <Route path="/domains/:id" element={<DomainDetailPage />} />
                            <Route path="/leads" element={<LeadsPage />} />
                            <Route path="/visits" element={<VisitsPage />} />
                            <Route path="/ad-sources" element={<AdSourcesPage />} />
                            <Route path="/admins" element={<AdminsPage />} />
                            <Route path="/constructor" element={<DraftsListPage />} />
                            <Route path="/drafts" element={<Navigate to="/constructor" replace />} />
                            <Route path="/sessions" element={<SessionsPage />} />
                            <Route path="/memory" element={<MemoryPage />} />
                            <Route path="/campaigns" element={<CampaignsListPage />} />
                            <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
                            <Route path="/pixels" element={<PixelsListPage />} />
                            <Route path="/advertisers" element={<AdvertisersListPage />} />
                            <Route path="/advertisers/:id" element={<AdvertiserDetailPage />} />
                            <Route path="/affiliates" element={<AffiliatesListPage />} />
                            <Route path="/affiliates/:id" element={<AffiliateDetailPage />} />
                            <Route path="/finance" element={<FinancePage />} />
                            <Route path="/conversions" element={<ConversionsPage />} />
                            <Route path="/unassigned" element={<UnassignedPage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
                <Toaster richColors position="top-right" closeButton />
            </AuthProvider>
        </QueryClientProvider>
    );
}
